import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { GridService } from '../shared/grid/grid.service';
import { GridModel } from '../shared/grid/grid.model';
import { GridExport } from '../models/gridExport.model';
import { SampleTemplate, SampleTemplateApplyResponse, SampleTemplateListResponse, SampleTemplateLookupItem, SampleTemplateUserDefault } from '../models/sampleTemplate.model';
import { SampleTypeEnum } from '../enums/sampleType.enum';

@Injectable({
  providedIn: 'root'
})
export class SampleTemplateService extends GridService<SampleTemplate> {

  constructor(http: HttpClient) {
    super('SampleTemplate', http);
  }

  override get(filter: string, orderBy: string, sort: string, page: number, pageSize: number, filterBy: string, headers?: HttpHeaders): Observable<GridModel<SampleTemplateListResponse>> {
    return this.http.get<GridModel<SampleTemplateListResponse>>(this.getApiUrl(`${this.controllerName}/GetList`), {
      params: this.createParamsWithFilterBy(filter, orderBy, sort, page, pageSize, filterBy),
      headers
    }).pipe(
      map(model => new GridModel<SampleTemplateListResponse>(
        model.data.map(row => ({ ...row, isLocked: !row.canManage })),
        model.count
      ))
    );
  }

  override export(request: GridExport, headers?: HttpHeaders): Observable<HttpResponse<Blob>> {
    return this.http.post(
      this.getApiUrl(`${this.controllerName}/Export`),
      request,
      { responseType: 'blob', observe: 'response', headers }
    );
  }

  apply(id: string): Observable<SampleTemplateApplyResponse> {
    return this.http.get<SampleTemplateApplyResponse>(
      this.getApiUrl(`${this.controllerName}/Apply`),
      { params: { id } }
    );
  }

  duplicate(id: string, name: string): Observable<SampleTemplate> {
    return this.http.post<SampleTemplate>(
      this.getApiUrl(`${this.controllerName}/Duplicate`),
      null,
      { params: { id, name } }
    );
  }

  getDefault(sampleType: SampleTypeEnum): Observable<SampleTemplate | null> {
    return this.http.get<SampleTemplate | null>(
      this.getApiUrl(`${this.controllerName}/GetDefault`),
      { params: { sampleType } }
    );
  }

  setDefault(dto: SampleTemplateUserDefault): Observable<SampleTemplateUserDefault> {
    return this.http.post<SampleTemplateUserDefault>(
      this.getApiUrl(`${this.controllerName}/SetDefault`),
      dto
    );
  }

  clearDefault(sampleType: SampleTypeEnum): Observable<boolean> {
    return this.http.post<boolean>(
      this.getApiUrl(`${this.controllerName}/ClearDefault`),
      null,
      { params: { sampleType } }
    );
  }

  rename(template: SampleTemplate, newName: string): Observable<Object> {
    return this.update({ ...template, name: newName });
  }

  setShared(template: SampleTemplate, isShared: boolean, organizationLevelId?: string | null): Observable<Object> {
    // The API's OrganizationLevelId is a non-nullable Guid: un-sharing passes null, which keeps the template's own level.
    const resolvedOrgLevelId = organizationLevelId ?? template.organizationLevelId;
    return this.update({ ...template, isShared, organizationLevelId: resolvedOrgLevelId });
  }

  getAllWithType(): Observable<SampleTemplateLookupItem[]> {
    return this.http.get<SampleTemplateLookupItem[]>(this.getApiUrl(`${this.controllerName}/GetAll?filterBy=`));
  }
}
